import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useKickAuth, kickModerationLoginUrl } from './providers/Auth/KickProvider';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import {
  $getRoot,
  $getSelection,
  $createTextNode,
  $createParagraphNode,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
} from 'lexical';

import { EmoteNode, $createEmoteNode, $isEmoteNode } from './EmoteNode';
import kickEmoteIcon from './images/kick-emotes-btn.svg';
import { log } from './lib/logger';
import { parseKickContent } from './lib/kickContent';
import { KickReplyBar, KickModerationPrompt, KickErrorBar } from './kick/KickPostBars';

export const MODERATION_PROMPT_KEY = 'kickModerationPromptDismissed';

// Rebuild editor nodes from message text, so a failed send goes back into the box with its emotes
function parseMessageToNodes(content) {
  return parseKickContent(content, { links: false })
    .filter((piece) => piece.type === 'emote' || piece.text)
    .map((piece) => (piece.type === 'emote'
      ? $createEmoteNode(piece.id, piece.name, piece.url)
      : $createTextNode(piece.text)));
}

// Lexical editor configuration - defined outside component to prevent recreation
const initialConfig = {
  namespace: 'KickChatEditor',
  theme: {
    paragraph: 'editor-paragraph',
    text: {
      bold: 'editor-text-bold',
      italic: 'editor-text-italic',
    },
  },
  onError: (error) => {
    console.error('Lexical error:', error);
  },
  nodes: [EmoteNode],
  editorState: null, // Start with completely empty editor
};

// Plugin to highlight selected emotes
function EmoteSelectionPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        
        // Remove previous selection highlights in this editor only (other Kick chats have their own)
        const rootElement = editor.getRootElement();
        if (!rootElement) return;
        rootElement.querySelectorAll('.editor-emote-node').forEach(node => node.classList.remove('selected'));
        
        if (selection) {
          // Only highlight emotes if we have an actual selection (not just a cursor position)
          // A collapsed selection means it's just a cursor, not a selection range
          if (!selection.isCollapsed()) {
            const selectedNodes = selection.getNodes();
            selectedNodes.forEach(node => {
              if ($isEmoteNode(node)) {
                const domNode = editor.getElementByKey(node.getKey());
                if (domNode) {
                  domNode.classList.add('selected');
                }
              }
            });
          }
        }
      });
    });
  }, [editor]);

  return null;
}

// Custom Placeholder Plugin with full control
function PlaceholderPlugin({ placeholderText = 'Type a message...' }) {
  const [editor] = useLexicalComposerContext();
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    // Check initial state
    editor.getEditorState().read(() => {
      const root = $getRoot();
      const textContent = root.getTextContent();
      const empty = textContent.trim().length === 0;
      setIsEmpty(empty);
    });

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const textContent = root.getTextContent();
        const empty = textContent.trim().length === 0;
        setIsEmpty(empty);
      });
    });
  }, [editor]);


  if (!isEmpty) return null;

  return (
    <div className="editor-placeholder" style={{ display: 'block', position: 'absolute', color: '#9ca3af' }}>
      {placeholderText}
    </div>
  );
}

// Plugin to handle Enter key for sending messages
function EnterKeyPlugin({ onSubmit, disabled, showMentions, onMentionSelect }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (!event.shiftKey && !disabled) {
          event.preventDefault();
          
          // If mentions popup is showing, select the highlighted mention instead of sending
          if (showMentions && onMentionSelect) {
            onMentionSelect();
            return true;
          }
          
          // Otherwise, send the message
          onSubmit();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onSubmit, disabled, showMentions, onMentionSelect]);

  return null;
}

// Plugin to handle Escape key for closing emote picker or canceling reply
function EscapeKeyPlugin({ onEscape, replyingTo, onCancelReply }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      (event) => {
        event.preventDefault();
        
        // Priority 1: Cancel reply if active
        if (replyingTo && onCancelReply) {
          onCancelReply();
          return true;
        }
        
        // Priority 2: Close emote picker
        onEscape();
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onEscape, replyingTo, onCancelReply]);

  return null;
}

// Plugin to disable editor when user is banned/timed out
function DisableEditorPlugin({ disabled }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return null;
}

// Plugin to handle Arrow keys for mention navigation
function ArrowKeyPlugin({ showMentions, selectedIndex, mentionCount, onNavigate }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Only intercept arrow keys when mentions are showing
    if (!showMentions) return;

    const unregisterDown = editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (event) => {
        event.preventDefault();
        const nextIndex = (selectedIndex + 1) % mentionCount;
        onNavigate(nextIndex);
        return true; // Handled
      },
      COMMAND_PRIORITY_HIGH // Higher priority to intercept before default behavior
    );

    const unregisterUp = editor.registerCommand(
      KEY_ARROW_UP_COMMAND,
      (event) => {
        event.preventDefault();
        const prevIndex = selectedIndex === 0 ? mentionCount - 1 : selectedIndex - 1;
        onNavigate(prevIndex);
        return true; // Handled
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      unregisterDown();
      unregisterUp();
    };
  }, [editor, showMentions, selectedIndex, mentionCount, onNavigate]);

  return null;
}

// Plugin to insert emotes
function EmoteInsertPlugin({ emoteToInsert, onEmoteInserted }) {
  const [editor] = useLexicalComposerContext();
  const insertCountRef = useRef(0);

  useEffect(() => {
    if (emoteToInsert) {
      insertCountRef.current += 1;
      
      editor.update(() => {
        const selection = $getSelection();
        const emoteNode = $createEmoteNode(
          emoteToInsert.id,
          emoteToInsert.name || emoteToInsert.tag || emoteToInsert.id,
          `https://files.kick.com/emotes/${emoteToInsert.id}/fullsize`
        );
        
        if (selection) {
          selection.insertNodes([emoteNode]);
        } else {
          const root = $getRoot();
          let lastParagraph = root.getLastChild();
          
          if (!lastParagraph || lastParagraph.getType() !== 'paragraph') {
            lastParagraph = $createParagraphNode();
            root.append(lastParagraph);
          }
          
          lastParagraph.append(emoteNode);
        }
        
        // CRITICAL: Move selection completely outside the emote node
        // by selecting the position after it in the paragraph
        const parent = emoteNode.getParent();
        if (parent && parent.getType() === 'paragraph') {
          // Get all children to find what's after the emote
          const nextSibling = emoteNode.getNextSibling();
          
          if (nextSibling) {
            // If there's a next sibling, select at its start
            nextSibling.selectStart();
          } else {
            // No next sibling, so we need to create a position after the emote
            // Use selectEnd on paragraph which will place cursor after all content
            parent.selectEnd();
          }
        }
      });
      
      // Manually extract content after inserting emote to ensure state is updated
      // This is needed because OnChangePlugin may not fire on initial insert
      setTimeout(() => {
        editor.getEditorState().read(() => {
          let content = '';
          const root = $getRoot();
          
          root.getChildren().forEach(paragraph => {
            paragraph.getChildren().forEach(node => {
              if ($isEmoteNode(node)) {
                content += node.getTextContent();
              } else {
                content += node.getTextContent();
              }
            });
            content += '\n';
          });
          
          const extractedContent = content.trim();
          // Trigger the parent's state update
          if (extractedContent) {
            // We need to call this outside the read() to avoid state updates during read
            Promise.resolve().then(() => onEmoteInserted(extractedContent));
          }
        });
      }, 0);
      
      editor.focus();
    }
  }, [editor, emoteToInsert, onEmoteInserted]);

  return null;
}

// Plugin to extract content
function ContentExtractionPlugin({ onContentChange }) {
  const [editor] = useLexicalComposerContext();

  const extractContent = useCallback(() => {
    let content = '';
    editor.getEditorState().read(() => {
      const root = $getRoot();
      
      
      // Walk through all nodes to build the content string
      root.getChildren().forEach(paragraph => {
        
        paragraph.getChildren().forEach(node => {
          
          if ($isEmoteNode(node)) {
            const emoteContent = node.getTextContent();
            content += emoteContent;
          } else {
            const textContent = node.getTextContent();
            content += textContent;
          }
        });
        content += '\n';
      });
    });
    return content.trim();
  }, [editor]);

  return (
    <OnChangePlugin
      onChange={() => {
        const content = extractContent();
        onContentChange(content);
      }}
      ignoreSelectionChange={true}
    />
  );
}

// Main Component
const KickChatPost = ({
  broadcasterId,
  slug,
  replyingTo,
  onCancelReply,
  identity,          // the viewer's identity in this channel (from KickChat's useKickChannelIdentity)
  identityLoading,
  restriction,       // { type: 'banned' } | { type: 'timeout', expiresAt } | null for the viewer in this channel
  recentChatters = [],
  moderationScopesMissing = false, // the viewer moderates this channel but hasn't granted the moderation scopes
}) => {
  const [error, setError] = useState('');
  // Dismissing the "Enable moderation tools" prompt lasts for this browser tab (the user panel keeps a link)
  const [moderationPromptDismissed, setModerationPromptDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(MODERATION_PROMPT_KEY) === '1';
    } catch {
      return false;
    }
  });
  const dismissModerationPrompt = useCallback(() => {
    setModerationPromptDismissed(true);
    try {
      sessionStorage.setItem(MODERATION_PROMPT_KEY, '1');
    } catch {
      // storage unavailable: dismissed until the page reloads
    }
  }, []);
  const [isAuthError, setIsAuthError] = useState(false);
  const [emotes, setEmotes] = useState([]);
  const [showEmotes, setShowEmotes] = useState(false);
  const [emoteToInsert, setEmoteToInsert] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [editorRef, setEditorRef] = useState(null);

  // Login state comes from KickProvider, so logging out anywhere updates this chat immediately
  const { isLoggedIn, checkAuthStatus } = useKickAuth();
  const isSubscriber = !!identity?.badges?.some((badge) => badge.type === 'subscriber' && badge.active);
  const checkingSubscription = !!identityLoading;

  // Autocomplete state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  // ⭐ MODERATION: Local state - NOT initialized from sessionStorage
  // Ban/timeout status can change while user is offline, so we only set it:
  // 1. When we receive a BANNED_ERROR from sending a message
  // 2. When we receive a WebSocket event (ban/unban/timeout/untimeout)
  const [isBanned, setIsBanned] = useState(false);
  const [timeoutExpiresAt, setTimeoutExpiresAt] = useState(null);

  // ⭐ MODERATION: Calculate placeholder text based on ban/timeout status
  const [placeholderText, setPlaceholderText] = useState('Type a message...');
  useEffect(() => {
    
    if (isBanned) {
      setPlaceholderText('You are permanently banned from this channel');
      return;
    }
    
    // Handle timeout with unknown duration or unknown status
    if (timeoutExpiresAt === 'unknown') {
      setPlaceholderText('You are currently banned or timed out');
      return;
    }
    
    if (timeoutExpiresAt) {
      const updateCountdown = () => {
        const now = new Date();
        const expires = new Date(timeoutExpiresAt);
        const diff = expires - now;

        if (diff <= 0) {
          setPlaceholderText('Type a message...');
          setTimeoutExpiresAt(null);
          return;
        }

        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        
        let timeString = '';
        if (hours > 0) {
          timeString = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        } else {
          timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
        
        setPlaceholderText(`You are timed out for ${timeString}`);
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);

      return () => clearInterval(interval);
    } else {
      setPlaceholderText('Type a message...');
    }
  }, [isBanned, timeoutExpiresAt]);

  // ⭐ MODERATION: The viewer's ban/timeout in *this* channel, derived by KickChat from moderation events.
  // (Restrictions learned from a BANNED_ERROR on send are set locally in handleSendMessage.)
  useEffect(() => {
    if (!restriction) {
      setIsBanned(false);
      setTimeoutExpiresAt(null);
    } else if (restriction.type === 'banned') {
      setIsBanned(true);
      setTimeoutExpiresAt(null);
    } else if (restriction.type === 'timeout') {
      setIsBanned(false);
      setTimeoutExpiresAt(restriction.expiresAt);
    }
  }, [restriction]);

  // Fetch emotes with metadata caching
  useEffect(() => {
    const fetchEmotes = async () => {
      if (!slug) {
        return;
      }

      try {
        // Check metadata cache first
        const cacheKey = `kick_emotes_meta_${slug}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          const now = Date.now();
          
          // Check if cache is still valid
          const channelCacheValid = now - parsed.channelTimestamp < 5 * 60 * 1000; // 5 minutes
          const globalCacheValid = now - parsed.globalTimestamp < 24 * 60 * 60 * 1000; // 24 hours
          
          if (channelCacheValid && globalCacheValid) {
            log.debug('Using cached emote metadata for', slug);
            setEmotes(parsed.emotes);
            return;
          }
        }

        log.debug(`Fetching emotes for channel: ${slug}`);
        
        const response = await axios.get(`https://kick.com/emotes/${slug}`);
        log.debug('Emotes response:', response.data);
        

        // Response is an array with 3 objects, but we need to identify them by properties
        const allEmotes = [];
        const now = Date.now();

        if (Array.isArray(response.data)) {
          response.data.forEach(section => {
            if (!section.emotes || !Array.isArray(section.emotes) || section.emotes.length === 0) {
              return; // Skip empty sections
            }

            // Identify section type by its properties
            let group = 'global';
            
            if (section.id === 'Emoji' || section.name === 'Emojis') {
              group = 'emoji';
            } else if (section.id === 'Global' || section.name === 'Global') {
              group = 'global';
            } else if (section.slug) {
              group = 'channel';
            }

            // Add emotes with the correct group
            section.emotes.forEach(emote => {
              const emoteData = {
                ...emote,
                group: group
              };

              // Add channel-specific metadata
              if (group === 'channel') {
                emoteData.channelName = section.slug;
                emoteData.subscribers_only = emote.subscribers_only;
              }

              allEmotes.push(emoteData);
            });
          });
        }

        log.debug('Emotes loaded:', {
          channel: allEmotes.filter(e => e.group === 'channel').length,
          global: allEmotes.filter(e => e.group === 'global').length,
          emoji: allEmotes.filter(e => e.group === 'emoji').length,
          total: allEmotes.length
        });

        // Cache the emote metadata (not images)
        const cacheData = {
          emotes: allEmotes,
          channelTimestamp: now,
          globalTimestamp: now,
          slug: slug
        };
        
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
          log.debug('Emote metadata cached for', slug);
        } catch (err) {
          console.warn('Failed to cache emote metadata:', err);
        }

        setEmotes(allEmotes);
      } catch (err) {
        console.error('Failed to fetch emotes:', err.message);
      }
    };

    fetchEmotes();
  }, [slug]);

  // Auto-focus editor when replying
  useEffect(() => {
    if (replyingTo && editorRef) {
      editorRef.focus();
    }
  }, [replyingTo, editorRef]);

  // Handle sending message
  const handleSendMessage = useCallback(async () => {
    
    if (!messageContent || !messageContent.trim()) {
      return;
    }


    // Store the message content before clearing
    const messageToSend = messageContent;
    
    // Immediately clear the editor, close emote picker, and clear errors (optimistic)
    setShowEmotes(false);
    setError('');
    setIsAuthError(false);
    
    if (editorRef) {
      editorRef.update(() => {
        const root = $getRoot();
        root.clear();
        root.append($createParagraphNode());
      });
    }


    // Send in background - no UI blocking
    try {
      // Build payload
      const payload = {
        broadcaster_user_id: broadcasterId,
        content: messageToSend,
      };

      // Add reply_to_message_id if replying
      if (replyingTo) {
        payload.reply_to_message_id = replyingTo.id;
      }

      log.debug('[Kick Chat] Sending:', payload);
      const response = await axios.post(
        `${process.env.REACT_APP_KICK_CHAT_SEND_SERVICE}/send`,
        payload,
        {
          withCredentials: true,
        }
      );
      log.debug('[Kick Chat] Sent:', response.data);

      // Clear reply state after successful send
      if (replyingTo && onCancelReply) {
        onCancelReply();
      }
    } catch (err) {
      console.error('❌ Error sending message:', err.response?.data || err.message);
      log.debug('[Kick Chat] Send failed, status', err.response?.status, err);
      
      // ⭐ MODERATION: Check if this is a ban error first
      const errorDataRaw = err.response?.data?.data;
      const errorDataStr = typeof errorDataRaw === 'string' 
        ? errorDataRaw 
        : JSON.stringify(err.response?.data || '');
      const isBanError = err.response?.status === 500 && 
                        (errorDataStr.includes('BANNED_ERROR') || errorDataStr.toLowerCase().includes('banned'));
      
      // Restore the message to the editor on failure (unless banned)
      if (editorRef && !isBanError) {
        editorRef.update(() => {
          const root = $getRoot();
          root.clear();
          const paragraph = $createParagraphNode();
          // Parse the message and recreate emote nodes
          const nodes = parseMessageToNodes(messageToSend);
          nodes.forEach(node => paragraph.append(node));
          root.append(paragraph);
        });
      }
      
      // If we get a 401 (Unauthorized), it's an auth issue
      if (err.response?.status === 401) {
        log.debug('🔐 [Kick Chat] Got 401 - Authentication required');
        setError('Session expired. Please login again.');
        setIsAuthError(true);

        // Let the provider re-check, so every chat's Chat/Login button reflects the real login state
        checkAuthStatus();
      }
      // ⭐ MODERATION: Check if user is banned/timed out from send error
      else if (err.response?.status === 500) {
        // Convert error data to string for checking
        const errorData = typeof err.response?.data?.data === 'string' 
          ? err.response.data.data 
          : JSON.stringify(err.response?.data || '');
        
        
        if (errorData.includes('BANNED_ERROR') || errorData.toLowerCase().includes('banned')) {
          log.debug('🚫 [Kick Chat] BANNED_ERROR detected - checking if ban or timeout');
          
          // Check if this is a timeout by looking for "timed out" or duration information
          const isTimeout = errorData.toLowerCase().includes('timed out') || 
                           errorData.toLowerCase().includes('timeout');
          
          // Try to extract duration/expiration from error message if available
          // Format might be: "BANNED_ERROR: You are timed out for X minutes"
          let timeoutDuration = null;
          const durationMatch = errorData.match(/(\d+)\s*minute/i);
          if (durationMatch) {
            timeoutDuration = parseInt(durationMatch[1]);
          }
          
          if (isTimeout && timeoutDuration) {
            // It's a timeout with known duration - calculate expiration and store it
            const expiresAt = new Date(Date.now() + timeoutDuration * 60000);
            log.debug(`✅ Detected timeout for ${timeoutDuration} minutes, expires at:`, expiresAt.toISOString());
            
            setIsBanned(false);
            setTimeoutExpiresAt(expiresAt.toISOString());
          } else if (isTimeout) {
            // It's a timeout but we don't know duration - show generic timeout message
            log.debug('✅ Detected timeout but no duration info - showing generic timeout message');
            
            setIsBanned(false);
            setTimeoutExpiresAt('unknown');  // Special value to indicate timeout without known expiration
          } else {
            // Can't determine if it's a ban or timeout - show generic message
            log.debug('✅ Cannot determine ban vs timeout - showing generic restricted message');
            
            setIsBanned(false);
            setTimeoutExpiresAt('unknown');  // Use 'unknown' for generic restricted status
          }
          
          // Do NOT show error message - the placeholder will show ban/timeout status
          // Do NOT restore message to editor - user is banned/timed out
          log.debug('✅ Ban/timeout status updated - input disabled, waiting for WebSocket event for full details');
        } else {
          // Not a ban error, show generic error
          log.debug('Non-ban 500 error:', errorData);
          setError(err.response?.data?.error || 'Failed to send message');
          setIsAuthError(false);
        }
      }
      else {
        log.debug('Non-401 error, status:', err.response?.status);
        setError(err.response?.data?.error || 'Failed to send message');
        setIsAuthError(false);
      }
    }
  }, [messageContent, broadcasterId, editorRef, checkAuthStatus, replyingTo, onCancelReply]);

  // Insert emote - keep picker open
  const insertEmote = useCallback((emote) => {
    setEmoteToInsert(emote);
  }, []);

  const handleEmoteInserted = useCallback((extractedContent) => {
    setEmoteToInsert(null);
    // Update messageContent if provided from manual extraction
    if (extractedContent) {
      setMessageContent(extractedContent);
    }
  }, []);

  const handleEscapeKey = useCallback(() => {
    if (showMentions) {
      setShowMentions(false);
    } else {
      setShowEmotes(false);
    }
  }, [showMentions]);

  // Handle @ mention detection and filtering
  useEffect(() => {
    if (!messageContent || !editorRef) {
      setShowMentions(false);
      return;
    }

    // Check if message contains @ and get the search term
    const match = messageContent.match(/@(\w*)$/);
    
    if (match) {
      const searchTerm = match[1].toLowerCase();
      
      // Filter recent chatters based on search term
      const filtered = recentChatters
        .filter(username => username.toLowerCase().startsWith(searchTerm))
        .slice(0, 4); // Show max 4 suggestions
      
      setMentionSuggestions(prevSuggestions => {
        // Only reset selected index if the suggestions list actually changed
        const suggestionsChanged = JSON.stringify(prevSuggestions) !== JSON.stringify(filtered);
        if (suggestionsChanged) {
          setSelectedMentionIndex(0);
        }
        return filtered;
      });
      setShowMentions(filtered.length > 0);
    } else {
      setShowMentions(false);
    }
  }, [messageContent, recentChatters, editorRef]);

  // Insert selected mention
  const insertMention = useCallback((username) => {
    if (!editorRef) return;

    editorRef.update(() => {
      const root = $getRoot();
      const paragraph = root.getLastChild();
      
      if (paragraph) {
        // Get current text
        const children = paragraph.getChildren();
        const lastChild = children[children.length - 1];
        
        if (lastChild) {
          const text = lastChild.getTextContent();
          // Replace @search with @username 
          const newText = text.replace(/@\w*$/, `@${username} `);
          lastChild.setTextContent(newText);
          
          // Move cursor to end
          paragraph.selectEnd();
        }
      }
    });

    setShowMentions(false);
    editorRef.focus();
  }, [editorRef]);

  // Handle selecting the currently highlighted mention (for Enter key)
  const handleSelectCurrentMention = useCallback(() => {
    if (mentionSuggestions.length > 0 && selectedMentionIndex >= 0) {
      const username = mentionSuggestions[selectedMentionIndex];
      insertMention(username);
    }
  }, [mentionSuggestions, selectedMentionIndex, insertMention]);

  // Handle arrow key navigation in mention list
  const handleMentionNavigate = useCallback((newIndex) => {
    setSelectedMentionIndex(newIndex);
  }, []);

  // Handle chat button click when not authenticated
  const handleChatButtonClick = useCallback(() => {
    if (isLoggedIn) {
      handleSendMessage();
    } else {
      // Redirect to OAuth login flow
      const BACKEND_URL = process.env.REACT_APP_KICK_OAUTH_SERVICE || 'http://localhost:4000';
      const currentUrl = window.location.href;
      window.location.href = `${BACKEND_URL}/kick?redirect=${encodeURIComponent(currentUrl)}`;
    }
  }, [isLoggedIn, handleSendMessage]);

  return (
    <div className="kick-chat-post">
      {replyingTo && <KickReplyBar replyingTo={replyingTo} onCancel={onCancelReply} />}

      {moderationScopesMissing && !moderationPromptDismissed && (
        <KickModerationPrompt enableUrl={kickModerationLoginUrl()} onDismiss={dismissModerationPrompt} />
      )}

      {error && (
        <KickErrorBar
          message={error}
          showLogin={isAuthError}
          onDismiss={() => {
            setError('');
            setIsAuthError(false);
          }}
        />
      )}

      <LexicalComposer initialConfig={initialConfig}>
        <div className="kick-chat-form">
          <div className="kick-chat-editor-container">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className="kick-chat-input"
                  aria-placeholder={placeholderText}
                  aria-disabled={isBanned || !!timeoutExpiresAt}
                />
              }
              placeholder={null}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <PlaceholderPlugin placeholderText={placeholderText} />
            <DisableEditorPlugin disabled={isBanned || !!timeoutExpiresAt} />
            <EmoteSelectionPlugin />
            <HistoryPlugin />
            <EnterKeyPlugin 
              onSubmit={handleSendMessage} 
              disabled={isBanned || !!timeoutExpiresAt}
              showMentions={showMentions}
              onMentionSelect={handleSelectCurrentMention}
            />
            <EscapeKeyPlugin 
              onEscape={handleEscapeKey} 
              replyingTo={replyingTo}
              onCancelReply={onCancelReply}
            />
            <ArrowKeyPlugin
              showMentions={showMentions}
              selectedIndex={selectedMentionIndex}
              mentionCount={mentionSuggestions.length}
              onNavigate={handleMentionNavigate}
            />
            <EmoteInsertPlugin
              emoteToInsert={emoteToInsert}
              onEmoteInserted={handleEmoteInserted}
            />
            <ContentExtractionPlugin onContentChange={setMessageContent} />
            <EditorRefPlugin setEditorRef={setEditorRef} />
          </div>

          <div className="input-actions">
            <button
              type="button"
              className="emote-toggle-btn"
              onClick={() => setShowEmotes(!showEmotes)}
              disabled={isBanned || !!timeoutExpiresAt}
            >
              <img src={kickEmoteIcon} alt="Kick Emotes" title="Kick Emotes" />
            </button>
            <button
              type="button"
              onClick={handleChatButtonClick}
              className={`chat-btn ${!isLoggedIn ? 'login' : ''}`}
              disabled={isBanned || !!timeoutExpiresAt}
            >
              {isLoggedIn ? 'Chat' : 'Login'}
            </button>
          </div>
        </div>

        {showMentions && mentionSuggestions.length > 0 && (
          <div className="mention-popup">
            <div className="mention-header">Users</div>
            <div className="mention-list">
              {mentionSuggestions.map((username, index) => (
                <div
                  key={username}
                  className={`mention-item ${index === selectedMentionIndex ? 'selected' : ''}`}
                  onClick={() => insertMention(username)}
                  onMouseEnter={() => setSelectedMentionIndex(index)}
                >
                  @{username}
                </div>
              ))}
            </div>
          </div>
        )}

        {showEmotes && (
          <div className="emote-popup">
            {/* Channel Emotes */}
            {emotes.filter(e => e.group === 'channel').length > 0 && (
              <>
                <div className="emote-group-titles">
                  <p>
                    Channel Emotes
                    {checkingSubscription && (
                      <span className="emote-picker-item not-subscriber">
                        (Checking...)
                      </span>
                    )}
                    {!checkingSubscription && !isSubscriber && (
                      <span className="emote-picker-item not-subscriber">
                        (Subscriber Only)
                      </span>
                    )}
                  </p>
                </div>
                <div className="emote-grid">
                  {emotes
                    .filter(e => e.group === 'channel')
                    .map(emote => {
                      const canUse = isSubscriber || !emote.subscribers_only;
                      return (
                        <img
                          key={`channel-${emote.id}`}
                          src={`https://files.kick.com/emotes/${emote.id}/fullsize`}
                          alt={emote.name}
                          title={`${emote.name}${emote.subscribers_only ? ' (Subscriber Only)' : ''}`}
                          onClick={() => canUse ? insertEmote(emote) : null}
                          loading="lazy"
                          className={`emote-picker-item ${!canUse ? 'not-subscriber' : ''}`}
                        />
                      );
                    })}
                </div>
              </>
            )}

            {/* Global Emotes */}
            {emotes.filter(e => e.group === 'global').length > 0 && (
              <>
                <div className="emote-group-titles">Global Emotes</div>
                <div className="emote-grid">
                  {emotes
                    .filter(e => e.group === 'global')
                    .map(emote => (
                      <img
                        key={`global-${emote.id}`}
                        src={`https://files.kick.com/emotes/${emote.id}/fullsize`}
                        alt={emote.name}
                        title={emote.name}
                        onClick={() => insertEmote(emote)}
                        loading="lazy"
                        className="emote-picker-item"
                      />
                    ))}
                </div>
              </>
            )}

            {/* Emoji */}
            {emotes.filter(e => e.group === 'emoji').length > 0 && (
              <>
                <div className="emote-group-titles">Emoji</div>
                <div className="emote-grid">
                  {emotes
                    .filter(e => e.group === 'emoji')
                    .map(emote => (
                      <img
                        key={`emoji-${emote.id}`}
                        src={`https://files.kick.com/emotes/${emote.id}/fullsize`}
                        alt={emote.name}
                        title={emote.name}
                        onClick={() => insertEmote(emote)}
                        loading="lazy"
                        className="emote-picker-item"
                      />
                    ))}
                </div>
              </>
            )}
          </div>
        )}
      </LexicalComposer>
    </div>
  );
};

// Helper plugin to get editor reference
function EditorRefPlugin({ setEditorRef }) {
  const [editor] = useLexicalComposerContext();
  
  useEffect(() => {
    setEditorRef(editor);
  }, [editor, setEditorRef]);
  
  return null;
}

export default KickChatPost;